import { keepPreviousData, queryOptions } from '@tanstack/react-query';
import { apiClient } from '@/api/axios';
import { API } from '@/api/endpoints';
import { STALE_TIME } from '@/api/staleTimes';
import { downloadApiFile } from '@/utils/download';
import {
  pcmpMisReportMembersSchema,
  pcmpMisStatusReportSchema,
  type PcmpMisReportMember,
  type PcmpMisStatusReport,
  type PcmpMisStatusReportParams,
} from '../types/pcmpMisStatusReport.types';
import { pcmpReportQueryKeys } from './pcmpReports.api';

/** Everyone who ever recorded an MIS decision, whatever the report's window. */
export const pcmpMisReportMembersQueryOptions = () =>
  queryOptions({
    queryKey: [...pcmpReportQueryKeys.all(), 'mis-status-members'] as const,
    queryFn: async (): Promise<PcmpMisReportMember[]> => {
      const res = await apiClient.get(API.pcmpReport.misStatusMembers);
      return pcmpMisReportMembersSchema.parse(res.data);
    },
    staleTime: STALE_TIME,
  });

export const pcmpMisStatusReportQueryOptions = (params: PcmpMisStatusReportParams) =>
  queryOptions({
    queryKey: [...pcmpReportQueryKeys.all(), 'mis-status', params] as const,
    queryFn: async (): Promise<PcmpMisStatusReport> => {
      const res = await apiClient.get(API.pcmpReport.misStatus, { params });
      return pcmpMisStatusReportSchema.parse(res.data);
    },
    placeholderData: keepPreviousData,
    staleTime: STALE_TIME,
  });

/** The API names the file the same way; its header is not readable cross-origin. */
export function pcmpMisStatusReportXlsxFilename(date: string): string {
  return `PCMP_MIS_Status_Report_${date}.xlsx`;
}

export function downloadPcmpMisStatusReportXlsx(
  params: PcmpMisStatusReportParams,
  date: string,
): Promise<void> {
  return downloadApiFile(API.pcmpReport.misStatusXlsx, pcmpMisStatusReportXlsxFilename(date), {
    ...params,
  });
}
